import type { ScanBalance } from "@/hooks/useScanBalance";
import type { TierId } from "@/lib/figVerifyTiers";

/**
 * Per-tier feature gating for FIG Verify outputs.
 *
 * Priority order:
 *  1. trial_active=true          → full Pro Seller experience
 *  2. subscription_tier='trial' && trial_active=false → expired, paywall
 *  3. freeRemaining > 0 (legacy) → full Pro Seller experience
 *  4. paid tier (collector/seller/pro_seller) + active/trialing → tier features
 *  5. everything else            → no access
 */
export type EffectiveTier = "free_trial" | "none" | TierId;

export interface TierFeatures {
  effectiveTier: EffectiveTier;
  /** DataForSEO-backed pricing guidance + per-marketplace recommendations. */
  canSeePricing: boolean;
  /** Real-market pricing synthesis via DataForSEO + Claude (Seller+). */
  canPricingGuidance: boolean;
  /** Download a PDF of the scan report. */
  canExportPdf: boolean;
  /** White-label (no FIG branding) PDF. */
  canWhiteLabelPdf: boolean;
  /** Send more than one photo per scan. */
  canBulkUpload: boolean;
  /** Max photos per single scan (UI hint). */
  maxPhotosPerScan: number;
  /** Export the sold-pieces CSV from Profile. */
  canExportSalesCsv: boolean;
  /** Priority human review on uncertain verdicts. */
  canPriorityReview: boolean;
}

const NO_ACCESS: TierFeatures = {
  effectiveTier: "none",
  canSeePricing: false,
  canPricingGuidance: false,
  canExportPdf: false,
  canWhiteLabelPdf: false,
  canBulkUpload: false,
  maxPhotosPerScan: 1,
  canExportSalesCsv: false,
  canPriorityReview: false,
};

const FULL_PRO: TierFeatures = {
  effectiveTier: "free_trial",
  canSeePricing: true,
  canPricingGuidance: true,
  canExportPdf: true,
  canWhiteLabelPdf: true,
  canBulkUpload: true,
  maxPhotosPerScan: 5,
  canExportSalesCsv: true,
  canPriorityReview: true,
};

const PAID_TIERS = new Set<string>(["collector", "seller", "pro_seller"]);

export function deriveFeatures(balance: ScanBalance | null): TierFeatures {
  if (!balance) return NO_ACCESS;

  // Active trial — full Pro Seller experience.
  if (balance.trialActive) return FULL_PRO;

  // Expired trial — paywall.
  if (balance.subscriptionTier === "trial") return NO_ACCESS;

  // Legacy free scans (pre-trial_active) — full access until exhausted.
  if (balance.freeRemaining > 0) return FULL_PRO;

  const tier = balance.subscriptionTier;
  const subActive =
    PAID_TIERS.has(tier) &&
    (balance.subscriptionStatus === "active" || balance.subscriptionStatus === "trialing");

  if (!subActive) return { ...NO_ACCESS, effectiveTier: tier as EffectiveTier };

  switch (tier) {
    case "collector":
      return {
        effectiveTier: "collector",
        canSeePricing: false,
        canPricingGuidance: false,
        canExportPdf: false,
        canWhiteLabelPdf: false,
        canBulkUpload: false,
        maxPhotosPerScan: 1,
        canExportSalesCsv: false,
        canPriorityReview: false,
      };
    case "seller":
      return {
        effectiveTier: "seller",
        canSeePricing: true,
        canPricingGuidance: true,
        canExportPdf: true,
        canWhiteLabelPdf: false,
        canBulkUpload: false,
        maxPhotosPerScan: 1,
        canExportSalesCsv: true,
        canPriorityReview: false,
      };
    case "pro_seller":
      return {
        effectiveTier: "pro_seller",
        canSeePricing: true,
        canPricingGuidance: true,
        canExportPdf: true,
        canWhiteLabelPdf: true,
        canBulkUpload: true,
        maxPhotosPerScan: 5,
        canExportSalesCsv: true,
        canPriorityReview: true,
      };
    default:
      return NO_ACCESS;
  }
}
